import type { Column } from '../../claim-history/simple-table'

import * as Sentry from '@sentry/react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Fragment, useCallback, useMemo, useReducer } from 'react'
import { FaStethoscope } from 'react-icons/fa'
import { MdChevronRight, MdSensors, MdChat, MdDelete } from 'react-icons/md'
import { Link } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'

import http from '../../../lib/http'
import { notify } from '../../../lib/Notification/actions'
import { useAppDispatch, useAppSelector } from '../../../store'
import * as Table from '../../claim-history/simple-table'
import { Copyable } from '../../layout/sidebar'
import { Checkbox } from '../../shared/NarusMaterial'
import { PageHeader } from '../components/page-header'
import { SearchInput } from '../components/search-input'
import styles from '../index.module.css'

interface MobileUser extends Record<string, unknown> {
  id: string
  last_name?: string | null
  first_name?: string | null
  email?: string | null
  phone?: string | null
  roles?: Role[] | null
  devices?: Device[] | null
  updated_at?: string | null
  messages_warning_has_appeared?: boolean | null
  symptoms_warning_has_appeared?: boolean | null
  symptoms_survey_reminded?: boolean | null
  created_at?: string | null
  symptoms_survey_reported?: boolean | null
  medications_warning_has_appeared?: boolean | null
  last_seen?: string | null
  devices_count?: number | null
  versions?: Version[] | null
}

interface Role {
  role?: string | null
  client?: string | null
  resource?: string | null
  patient_id?: string | null
}

interface Device {
  arn?: string | null
  time?: string | null
  token?: string | null
  locale?: string | null
  platform?: string | null
}

interface Version {
  version?: string | null
  accepted?: string | null
}

export function nonNullablePredicate<T> (value: T | null | undefined): value is T {
  return value != null
}

type QueryKey = [
  'mobile-users',
  SearchParams
]

interface QueryData {
  data: MobileUser[]
  total: number
  count: number
}

interface State {
  searchParams: SearchParams
}

type Action =
  | { type: 'query/set', payload: string }
  | { type: 'withoutTerminated/set', payload: boolean }
  | { type: 'filters/set', payload: SearchParams['filters'] }
  | { type: 'filters/merge', payload: Partial<SearchParams['filters']> }
  | { type: 'filters/clear' }
  | { type: 'sort/set', payload: Sort }
  | { type: 'pagination/page/set', payload: number }
  | { type: 'pagination/page-size/set', payload: number }

interface SearchParams {
  query: string

  filters: {
    roles: string[]
  }

  sort: Sort
  pagination: Pagination
  withoutTerminated: boolean
}

let sortKeys = [
  'name',
  'email',
  'phone',
  'devices_count',
  'last_seen'
] as const

type SortKey = typeof sortKeys[number]
type SortDirection = 'asc' | 'desc'

interface Sort {
  field: SortKey
  direction: SortDirection
}

interface Pagination {
  page: number
  pageSize: number
}

let initialState: State = {
  searchParams: {
    query: '',
    filters: {
      roles: []
    },
    sort: {
      field: 'last_seen',
      direction: 'desc'
    },
    pagination: {
      page: 0,
      pageSize: 10
    },
    withoutTerminated: true
  }
}

function reducer (state: State, action: Action): State {
  switch (action.type) {
    case 'query/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }
    case 'withoutTerminated/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          withoutTerminated: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/merge': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          filters: {
            ...state.searchParams.filters,
            ...action.payload
          },
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'filters/clear': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          query: initialState.searchParams.query,
          filters: initialState.searchParams.filters,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'sort/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          sort: action.payload,
          pagination: {
            ...state.searchParams.pagination,
            page: 0
          }
        }
      }
    }

    case 'pagination/page/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            page: action.payload
          }
        }
      }
    }

    case 'pagination/page-size/set': {
      return {
        ...state,
        searchParams: {
          ...state.searchParams,
          pagination: {
            ...state.searchParams.pagination,
            pageSize: action.payload,
            page: 0
          }
        }
      }
    }

    default: {
      return state
    }
  }
}

let roleMapper: Record<string, string> = {
  employee: 'Employee',
  patient: 'Patient'
}

let sortMapper: Record<string, string> = {
  name: 'last_name'
}

let platformMapper: Record<string, string> = {
  ios: 'iOS',
  android: 'Android'
}

// let filterMapper: Record<string, string> = {
//   client_name: 'client_id'
// }

function isDefaultSearchParams (searchParams: Pick<SearchParams, 'query' | 'filters'>): boolean {
  return (
    searchParams.query === initialState.searchParams.query &&
    searchParams.filters.roles.length === initialState.searchParams.filters.roles.length &&
    searchParams.filters.roles.every(role => initialState.searchParams.filters.roles.includes(role))
  )
}

export function MobileUsersPage (): JSX.Element {
  let [ state, dispatch ] = useReducer(reducer, initialState)
  let [ debouncedSearchQuery ] = useDebounceValue(state.searchParams.query, 500)

  // let appDispatch = useAppDispatch()

  let query = useQuery<unknown, unknown, QueryData, QueryKey>({
    queryKey: [
      'mobile-users',
      { ...state.searchParams, query: debouncedSearchQuery }
    ],

    async queryFn ({ queryKey }) {
      let [ , searchParams ] = queryKey
      let { query, filters, sort, pagination, withoutTerminated } = searchParams
      let normalizedQuery = query.trim().toLowerCase()

      let sp = new URLSearchParams([
        [ 'count', pagination.pageSize.toString() ],
        [ 'page', pagination.page.toString() ],
        [ 'on', sortMapper[sort.field] ?? sort.field ],
        [ 'dir', sort.direction ],
        ...filters.roles.map(v => [ 'filters.role', v ]),
        ...(normalizedQuery.length > 0 ? [[ 'search', normalizedQuery ]] : []),
        [ 'withoutTerminated', String(withoutTerminated) ]
      ])

      let response = await http.getRequest(`/admin/mobile?${sp.toString()}`)
      return response
    },

    placeholderData: keepPreviousData
  })

  let columns = useMemo<Array<Column<MobileUser>>>(() => [
    {
      name: 'name',
      header: 'Name',
      isSortable: true,
      getValue (entry) {
        return [ entry.first_name?.trim(), entry.last_name?.trim() ]
          .filter(Boolean)
          .join(' ')
      }
    },
    {
      name: 'email',
      header: 'Email',
      isSortable: true
    },
    {
      name: 'phone',
      header: 'Phone',
      isSortable: true
    },
    {
      name: 'roles',
      header: 'Roles',
      isSortable: false,
      getValue (entry) {
        return (
          entry.roles
            ?.map(role => role.role)
            .filter(nonNullablePredicate)
            .map(role => roleMapper[role] ?? role)
            .sort((a, b) => a.localeCompare(b))
            .join(', ') ?? ''
        ) || null
      }
    },
    {
      name: 'devices_count',
      header: 'Devices',
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (entry) {
        return (entry.devices_count ?? 0) || null
      }
    },
    {
      name: 'last_seen',
      header: 'Last Seen',
      isSortable: true,
      initialSortDirection: 'desc',
      getValue (entry) {
        if (typeof entry.last_seen !== 'string' || !entry.last_seen.trim().length) {
          return null
        }

        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {Table.formatTime(entry.last_seen)}
          </span>
        )
      }
    }
  ], [])

  let filters = useMemo(() => {
    return [
      {
        name: 'roles',
        value: state.searchParams.filters.roles,
        options: [
          { value: 'employee', label: roleMapper.employee },
          { value: 'patient', label: roleMapper.patient }
        ]
      }
    ]
  }, [ state.searchParams.filters.roles, state.searchParams ])

  let handleFilterChange = useCallback(<T extends keyof MobileUser>(name: T, value: Array<MobileUser[T]>): void => {
    dispatch({
      type: 'filters/merge',
      payload: {
        [name]: value
      }
    })
  }, [])

  let handleFiltersClear = useCallback((): void => {
    dispatch({
      type: 'filters/clear'
    })
  }, [])

  let handleSortChange = useCallback((field: keyof MobileUser, direction: SortDirection): void => {
    function isSortKey (key: unknown): key is SortKey {
      return (sortKeys as readonly unknown[]).includes(key)
    }

    if (!isSortKey(field)) {
      return
    }

    dispatch({
      type: 'sort/set',
      payload: {
        field,
        direction
      }
    })
  }, [])

  let handlePageChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page/set',
      payload: value - 1
    })
  }, [])

  let handlePageSizeChange = useCallback((value: number): void => {
    dispatch({
      type: 'pagination/page-size/set',
      payload: value
    })
  }, [])

  let pagination = useMemo(() => {
    return {
      ...state.searchParams.pagination,
      totalFiltered: query.data?.total ?? 0,
      totalUnfiltered: query.data?.total ?? 0
    }
  }, [ state.searchParams.pagination, query.data ])

  return (
    <div>
      <PageHeader className={styles.pageHeader}>
        <h1>
          <span>Mobile Users</span>
        </h1>
        <SearchInput
          value={state.searchParams.query}
          placeholder='Search by name, email, phone and roles'
          onChange={event => {
            dispatch({
              type: 'query/set',
              payload: event.target.value
            })
          }}
          onFiltersClear={isDefaultSearchParams(state.searchParams) ? undefined : handleFiltersClear}
        />
        <Checkbox
          value={!state.searchParams.withoutTerminated}
          label='With terminated'
          onChange={value => {
            dispatch({
              type: 'withoutTerminated/set',
              payload: !value
            })
          }}
        />
      </PageHeader>

      <Table.Table
        data={query.data?.data ?? []}
        queryStatus={query.status}
        fetchStatus={query.fetchStatus}
        noDataPlaceholder='No mobile users found'
        forceClearFilters={!isDefaultSearchParams(state.searchParams)}
        columns={columns}
        filters={filters}
        sort={state.searchParams.sort}
        pagination={pagination}
        onFilterChange={handleFilterChange}
        onFiltersClear={handleFiltersClear}
        onSortChange={handleSortChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        expandingComponent={(item, state, setState) => {
          return (
            <ExpandingComponent
              item={item}
              onAction={() => {
                query.refetch()
                  .then(() => {
                    setState(item.id)
                  })
                  .catch(err => {
                    console.error(err)
                    Sentry.captureException(err)
                  })
              }}
            />
          )
        }}
      />
    </div>
  )
}

interface ExpandingComponentProps {
  item: MobileUser
  onAction?: () => void
}

function ExpandingComponent ({ item, onAction }: ExpandingComponentProps): JSX.Element {
  let [ rolesExpanded, toggleRolesExpanded ] = useReducer((state: boolean) => !state, false)
  let [ devicesExpanded, toggleDevicesExpanded ] = useReducer((state: boolean) => !state, false)

  let appDispatch = useAppDispatch()

  let role = useAppSelector(state => state?.auth?.profile?.app_metadata?.roles?.meta?.role ?? null)
  let isAdmin = [ 'administrator', 'executive_director', 'medical_director' ].includes(role ?? '')
  let mobileManagement: string = useAppSelector(state => state?.globalVars?.permissions?.mobile_management ?? 'none')
  let hasAccessToActions = isAdmin || mobileManagement === 'write'

  return (
    <div className={styles.expandableRowContent}>
      <div className={styles.row}>
        <div className={styles.details}>
          <span className={styles.label}>Name</span>
          <span className={styles.value}>
            {[ item.first_name, item.last_name ].filter(nonNullablePredicate).join(' ')}
          </span>

          <span className={styles.label}>Email</span>
          <span className={styles.value}>
            {typeof item.email === 'string' && (
              <Copyable value={item.email}>
                {item.email}
              </Copyable>
            )}

            {typeof item.email !== 'string' && '—'}
          </span>

          <span className={styles.label}>Phone</span>
          <span className={styles.value}>
            {typeof item.phone === 'string' && (
              <Copyable value={item.phone}>
                {item.phone}
              </Copyable>
            )}

            {typeof item.phone !== 'string' && '—'}
          </span>

          <span className={styles.label}>ID</span>
          <span className={styles.value}>
            <Copyable value={item.id}>
              {item.id}
            </Copyable>
          </span>

          <span className={styles.label}>Created</span>
          <span className={styles.value}>
            {typeof item.created_at === 'string' && Table.formatTime(item.created_at)}
            {typeof item.created_at !== 'string' && '—'}
          </span>

          <span className={styles.label}>Updated</span>
          <span className={styles.value}>
            {typeof item.updated_at === 'string' && Table.formatTime(item.updated_at)}
            {typeof item.updated_at !== 'string' && '—'}
          </span>
        </div>

        <div className={styles.details}>
          <span className={styles.label}>Messages</span>
          <span className={styles.value}>
            {item.messages_warning_has_appeared ? 'Warning has appeared' : 'Warning has not appeared'}
          </span>

          <span className={styles.label}>Symptoms</span>
          <span className={styles.value}>
            {item.symptoms_warning_has_appeared ? 'Warning has appeared' : 'Warning has not appeared'}
          </span>

          <span className={styles.label}>Symptoms Survey Reported</span>
          <span className={styles.value}>
            {item.symptoms_survey_reported ? 'Reported' : 'Not reported'}
          </span>

          <span className={styles.label}>Symptoms Survey Reminded</span>
          <span className={styles.value}>
            {item.symptoms_survey_reminded ? 'Reminded' : 'Not reminded'}
          </span>

          <span className={styles.label}>Accepted TOS</span>
          <span className={styles.value}>
            {(item.versions?.length ?? 0) === 0 && 'Not accepted'}

            {(item.versions?.length ?? 0) > 0 && (
              <div
                className={styles.details}
                style={{ gap: '0.75rem 0.625rem' }}
              >
                {item.versions?.map(version => (
                  <Fragment key={version.accepted}>
                    {typeof version.version === 'string' && (
                      <span
                        className={styles.version}
                        title={`TOS version ${version.version}`}
                      >
                        {version.version}
                      </span>
                    )}

                    {typeof version.version !== 'string' && (
                      <div />
                    )}

                    <span>
                      {typeof version.accepted === 'string' && Table.formatTime(version.accepted)}
                      {typeof version.accepted !== 'string' && '—'}
                    </span>
                  </Fragment>
                ))}
              </div>
            )}
          </span>
        </div>
      </div>

      {(item.roles?.length ?? 0) > 0 && (
        <div className={styles.c}>
          <div className={styles.r}>
            <button
              type='button'
              className={styles.collapseButton}
              onClick={toggleRolesExpanded}
            >
              <span className={styles.sectionHeader}>
                Roles
              </span>

              <MdChevronRight
                size={20}
                style={{
                  transform: `rotate(${rolesExpanded ? 90 : 0}deg) translateY(1px)`
                }}
              />
            </button>
          </div>

          {rolesExpanded && (
            <div
              className={styles.c}
              style={{ marginTop: '1rem', gap: '1rem' }}
            >
              {item.roles?.filter(nonNullablePredicate).map((role, i, a) => (
                <Fragment key={role.role}>
                  <div className={styles.details}>
                    <span className={styles.label}>Role</span>
                    <span className={styles.value}>
                      {roleMapper[role.role ?? ''] ?? role.role}
                    </span>

                    <span className={styles.label}>Client</span>
                    <span className={styles.value}>
                      {role.client ?? '—'}
                    </span>

                    <span className={styles.label}>Patient</span>
                    <span className={styles.value}>
                      <Link
                        to={`/patient/${role.client ?? ''}/${role.patient_id ?? ''}/patientFolder`}
                      >
                        {[ item.first_name, item.last_name ].filter(nonNullablePredicate).join(' ')}
                      </Link>
                    </span>

                    <span className={styles.label}>Resource</span>
                    <span className={styles.value}>
                      {typeof role.resource === 'string' && (
                        <Copyable value={role.resource}>
                          {role.resource}
                        </Copyable>
                      )}

                      {typeof role.resource !== 'string' && '—'}
                    </span>
                  </div>

                  {i < a.length - 1 && (
                    <hr />
                  )}
                </Fragment>
              ))}
            </div>
          )}
        </div>
      )}

      {(item.devices?.length ?? 0) > 0 && (
        <div className={styles.c}>
          <div className={styles.r}>
            <button
              type='button'
              className={styles.collapseButton}
              onClick={toggleDevicesExpanded}
            >
              <span className={styles.sectionHeader}>
                Devices
              </span>

              <MdChevronRight
                size={20}
                style={{
                  transform: `rotate(${devicesExpanded ? 90 : 0}deg) translateY(1px)`
                }}
              />
            </button>
          </div>

          {devicesExpanded && (
            <div
              className={styles.c}
              style={{ marginTop: '1rem', gap: '1rem' }}
            >
              <div className={styles.details}>
                <span
                  className={styles.label}
                  style={{ minWidth: '55px' }}
                >
                  All
                </span>
                <span className={styles.value}>
                  {platformMapper.ios}: {item.devices?.filter(device => device.platform === 'ios').length ?? 0}

                  <span style={{ padding: '0 0.5rem' }}>
                    •
                  </span>

                  {platformMapper.android}: {item.devices?.filter(device => device.platform === 'android').length ?? 0}

                  {hasAccessToActions && (
                    <div className={styles.actionButtons}>
                      <button
                        type='button'
                        className={styles.actionButton}
                        onClick={() => {
                          http.postRequest('/admin/mobile/ping', {
                            id: item.id
                          })
                            .then(() => {
                              appDispatch(notify('Devices pinged', false))
                            })
                            .catch((err: unknown) => {
                              console.error(err)
                              Sentry.captureException(err)
                              appDispatch(notify('Failed to ping devices', true))
                            })
                        }}
                        title='Ping all devices'
                      >
                        <MdSensors
                          size={20}
                          color='#fff'
                        />
                      </button>

                      <button
                        type='button'
                        className={styles.actionButton}
                        onClick={() => {
                          http.postRequest('/admin/mobile/messages', {
                            id: item.id
                          })
                            .then(() => {
                              appDispatch(notify('Messages notifications sent', false))
                            })
                            .catch((err: unknown) => {
                              console.error(err)
                              Sentry.captureException(err)
                              appDispatch(notify('Failed to send messages notifications', true))
                            })
                        }}
                        title='Send message notification to all devices'
                      >
                        <MdChat
                          size={20}
                          color='#fff'
                          style={{
                            transform: 'scale(0.8) translateY(1px)'
                          }}
                        />
                      </button>

                      <button
                        type='button'
                        className={styles.actionButton}
                        onClick={() => {
                          http.postRequest('/admin/mobile/symptoms', {
                            id: item.id
                          })
                            .then(() => {
                              appDispatch(notify('Symptoms notifications sent', false))
                            })
                            .catch((err: unknown) => {
                              console.error(err)
                              Sentry.captureException(err)
                              appDispatch(notify('Failed to send symptoms notifications', true))
                            })
                        }}
                        title='Send symptoms notification to all devices'
                      >
                        <FaStethoscope
                          size={20}
                          color='#fff'
                          style={{
                            transform: 'scale(0.8)'
                          }}
                        />
                      </button>

                      <button
                        type='button'
                        className={styles.actionButton}
                        style={{
                          backgroundColor: '#d0143a'
                        }}
                        onClick={() => {
                          let confirmed = window.confirm('Are you sure you want to delete all devices?')

                          if (!confirmed) {
                            return
                          }

                          http.postRequest('/admin/mobile/remove', {
                            id: item.id
                          })
                            .then(() => {
                              onAction?.()
                              appDispatch(notify('All devices deleted', false))
                            })
                            .catch((err: unknown) => {
                              console.error(err)
                              Sentry.captureException(err)
                              appDispatch(notify('Failed to delete all device', true))
                            })
                        }}
                        title='Delete all devices'
                      >
                        <MdDelete
                          size={20}
                          color='#fff'
                          style={{
                            transform: 'scale(0.95)'
                          }}
                        />
                      </button>
                    </div>
                  )}
                </span>
              </div>

              <hr />

              {item.devices?.filter(nonNullablePredicate).map((device, i, a) => (
                <Fragment key={device.arn}>
                  <div className={styles.details}>
                    <span className={styles.label}>Platform</span>
                    <span className={styles.value}>
                      {platformMapper[device.platform ?? ''] ?? device.platform ?? '—'}

                      {hasAccessToActions && (
                        <div
                          className={styles.actionButtons}
                          style={{
                            paddingLeft: device.platform === 'ios'
                              ? '7.325rem'
                              : '5.7125rem'
                          }}
                        >
                          <button
                            type='button'
                            className={styles.actionButton}
                            onClick={() => {
                              http.postRequest('/admin/mobile/ping', {
                                id: item.id,
                                device
                              })
                                .then(() => {
                                  appDispatch(notify('Device pinged', false))
                                })
                                .catch((err: unknown) => {
                                  console.error(err)
                                  Sentry.captureException(err)
                                  appDispatch(notify('Failed to ping device', true))
                                })
                            }}
                            title='Ping this device'
                          >
                            <MdSensors
                              size={20}
                              color='#fff'
                            />
                          </button>

                          <button
                            type='button'
                            className={styles.actionButton}
                            onClick={() => {
                              http.postRequest('/admin/mobile/messages', {
                                id: item.id,
                                device
                              })
                                .then(() => {
                                  appDispatch(notify('Messages notification sent', false))
                                })
                                .catch((err: unknown) => {
                                  console.error(err)
                                  Sentry.captureException(err)
                                  appDispatch(notify('Failed to send messages notification', true))
                                })
                            }}
                            title='Send message notification to this device'
                          >
                            <MdChat
                              size={20}
                              color='#fff'
                              style={{
                                transform: 'scale(0.8) translateY(1px)'
                              }}
                            />
                          </button>

                          <button
                            type='button'
                            className={styles.actionButton}
                            onClick={() => {
                              http.postRequest('/admin/mobile/symptoms', {
                                id: item.id,
                                device
                              })
                                .then(() => {
                                  appDispatch(notify('Symptoms notification sent', false))
                                })
                                .catch((err: unknown) => {
                                  console.error(err)
                                  Sentry.captureException(err)
                                  appDispatch(notify('Failed to send symptoms notification', true))
                                })
                            }}
                            title='Send symptoms notification to this device'
                          >
                            <FaStethoscope
                              size={20}
                              color='#fff'
                              style={{
                                transform: 'scale(0.8)'
                              }}
                            />
                          </button>

                          <button
                            type='button'
                            className={styles.actionButton}
                            style={{
                              backgroundColor: '#d0143a'
                            }}
                            onClick={() => {
                              let confirmed = window.confirm('Are you sure you want to delete this device?')

                              if (!confirmed) {
                                return
                              }

                              http.postRequest('/admin/mobile/remove', {
                                id: item.id,
                                device
                              })
                                .then(() => {
                                  onAction?.()
                                  appDispatch(notify('Device deleted', false))
                                })
                                .catch((err: unknown) => {
                                  console.error(err)
                                  Sentry.captureException(err)
                                  appDispatch(notify('Failed to delete device', true))
                                })
                            }}
                            title='Delete this device'
                          >
                            <MdDelete
                              size={20}
                              color='#fff'
                              style={{
                                transform: 'scale(0.95)'
                              }}
                            />
                          </button>
                        </div>
                      )}
                    </span>

                    <span className={styles.label}>Token</span>
                    <span
                      className={styles.value}
                      style={{ overflow: 'hidden' }}
                    >
                      {typeof device.token === 'string' && (
                        <Copyable
                          value={device.token}
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {device.token}
                        </Copyable>
                      )}

                      {typeof device.token !== 'string' && '—'}
                    </span>

                    <span className={styles.label}>ARN</span>
                    <span className={styles.value}>
                      {typeof device.arn === 'string' && (
                        <Copyable value={device.arn}>
                          {device.arn}
                        </Copyable>
                      )}

                      {typeof device.arn !== 'string' && '—'}
                    </span>

                    <span className={styles.label}>Time</span>
                    <span className={styles.value}>
                      {typeof device.time === 'string' && Table.formatTime(device.time)}
                      {typeof device.time !== 'string' && '—'}
                    </span>

                    <span className={styles.label}>Locale</span>
                    <span className={styles.value}>
                      {device.locale ?? '—'}
                    </span>
                  </div>

                  {i < a.length - 1 && (
                    <hr />
                  )}
                </Fragment>
              ))}
            </div>
          )}

        </div>
      )}

    </div>
  )
}
